import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import apiService from '../services/apiService';
import './UserCreate.css';
import { toInitialDateTime, isValidDateTime } from '../components/DateTimeInputBR';

// core.companies.transcription_engine — a API aceita também os motores de teste do transcriber (nemotron, gemini)
const MOTORES = {
    elevenlabs: 'ElevenLabs',
    whisper: 'Whisper (transcriber da Aliado)',
};

const CompanyEdit = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { company, scrollY } = location.state || {};
    const backUrl = scrollY ? `/users?scrollY=${scrollY}` : '/users';

    const [companyBucket, setCompanyBucket] = useState(company?.bucket_name || '');
    const [defaultIaModel, setDefaultIaModel] = useState(company?.default_ia_model || '');
    const [transcriptionEngine, setTranscriptionEngine] = useState(company?.transcription_engine || 'elevenlabs');
    // o setter saiu com o campo (§2.7): o valor e lido no submit e reenviado igual
    const [unlockDate] = useState(toInitialDateTime(company?.unlock_date));
    const [iaModels, setIaModels] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    useEffect(() => {
        apiService.getIaModels().then((models) => setIaModels(models || []));
    }, []);

    // ---- Régua de atendimento válido -------------------------------------------------------
    // O mapa é por EMPRESA e tem VIGÊNCIA: salvar cria uma regra nova valendo do dia seguinte, e
    // o que já foi registrado continua com a régua que valia no dia. O modal existe para que isso
    // seja uma decisão consciente e não uma descoberta depois.
    // Mesma leitura do resto do admin (PromptEditor): a chave é `aliado_auth`, e o id fica em
    // `.user.user_id`. Ler de `userData` devolvia undefined -> 403 no endpoint -> painel vazio.
    const userId = (() => {
        try { return JSON.parse(localStorage.getItem('aliado_auth'))?.user?.user_id; } catch (e) { return null; }
    })();
    const [regra, setRegra] = useState(null);
    const [baldes, setBaldes] = useState({});
    const [confirmando, setConfirmando] = useState(false);
    const [salvandoRegra, setSalvandoRegra] = useState(false);

    const carregarRegra = React.useCallback(() => {
        if (!company?.subdomain) return;
        apiService.pointsClassificationRules({ userId, subdomain: company.subdomain })
            .then((d) => {
                setRegra(d);
                const m = {};
                ['valid', 'invalid', 'none'].forEach((b) => (d.buckets?.[b] || []).forEach((c) => { m[c] = b; }));
                if (d.catch_all) m['*'] = d.catch_all;
                setBaldes(m);
            })
            .catch((e) => {
                // Nunca engolir: falha aqui some com o painel inteiro e parece "não configurado".
                setRegra(null);
                setMessage({ type: 'error', text: e?.response?.data?.detail || 'Não foi possível carregar a régua de atendimento válido.' });
            });
    }, [company?.subdomain, userId]);

    useEffect(() => { carregarRegra(); }, [carregarRegra]);

    // Data em que a alteração entra em vigor: amanhã em America/Sao_Paulo, o mesmo
    // CURRENT_DATE + 1 que o backend grava. Calculada aqui porque `next` só existe quando JÁ há
    // uma alteração agendada — e na primeira vez o modal precisa mostrar a data, não "amanhã".
    const vigenciaNova = (() => {
        const hojeSP = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Sao_Paulo' }));
        hojeSP.setDate(hojeSP.getDate() + 1);
        return hojeSP.toISOString().slice(0, 10);
    })();

    const alterou = regra && Object.entries(baldes).some(([c, b]) => {
        const orig = c === '*' ? regra.catch_all
            : ['valid', 'invalid', 'none'].find((k) => (regra.buckets?.[k] || []).includes(c));
        return orig !== b;
    });

    const salvarRegra = async () => {
        setSalvandoRegra(true);
        try {
            const items = Object.entries(baldes).map(([classification, counts_as]) => ({ classification, counts_as }));
            const r = await apiService.pointsSaveClassificationRules({ userId, subdomain: company.subdomain, items });
            setConfirmando(false);
            setMessage({ type: 'success', text: `Régua salva. Passa a valer em ${r.valid_from}; atendimento anterior não muda.` });
            carregarRegra();
        } catch (e) {
            setMessage({ type: 'error', text: e?.response?.data?.detail || 'Erro ao salvar a régua' });
        } finally { setSalvandoRegra(false); }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage({ type: '', text: '' });

        if (!companyBucket.trim()) {
            setMessage({ type: 'error', text: 'O bucket é obrigatório.' });
            return;
        }
        if (!defaultIaModel) {
            setMessage({ type: 'error', text: 'Selecione o modelo de IA padrão.' });
            return;
        }
        const ud = (unlockDate || '').trim();
        if (ud && !isValidDateTime(ud)) {
            setMessage({ type: 'error', text: 'Data final de aquecimento inválida. Use o formato AAAA-MM-DD HH:MM:SS (ex.: 2026-07-16 00:00:00).' });
            return;
        }

        try {
            setSubmitting(true);
            const payload = {
                company_id: company.company_id ?? company.id,
                companyBucket: companyBucket.trim(),
                default_ia_model: defaultIaModel,
                transcription_engine: transcriptionEngine,
            };
            if (ud) {
                payload.unlock_date = ud;
            } else {
                payload.clear_unlock_date = true; // permite salvar sem unlock_date (grava NULL)
            }
            const result = await apiService.updateCompany(payload);

            if (result.success) {
                setMessage({ type: 'success', text: `Empresa "${company?.name || ''}" atualizada com sucesso!` });
            } else {
                setMessage({ type: 'error', text: result.message || 'Erro ao editar empresa.' });
            }
        } catch (err) {
            setMessage({ type: 'error', text: 'Erro ao editar empresa. Tente novamente.' });
        } finally {
            setSubmitting(false);
        }
    };

    if (!company) {
        return (
            <div className="user-create">
                <div className="user-create-header">
                    <button className="btn-back" onClick={() => navigate('/users')}>&#8592; Voltar</button>
                    <h1>Editar Empresa</h1>
                </div>
                <div className="alert alert-error">Empresa não informada. Volte e selecione uma empresa.</div>
            </div>
        );
    }

    return (
        <div className="user-create">
            <div className="user-create-header">
                <button className="btn-back" onClick={() => navigate(backUrl)}>
                    &#8592; Voltar
                </button>
                <h1>Editar Empresa — {company.name}</h1>
            </div>

            {message.text && (
                <div className={`alert alert-${message.type}`}>
                    {message.text}
                </div>
            )}

            <form className="user-create-form" onSubmit={handleSubmit}>
                <div className="form-row">
                    <div className="form-group">
                        <label>Empresa</label>
                        <input type="text" className="form-input" value={company.name || ''} readOnly disabled />
                    </div>
                    <div className="form-group">
                        <label>Subdomínio</label>
                        <input type="text" className="form-input" value={company.subdomain || ''} readOnly disabled />
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="companyBucket">Bucket Name</label>
                        <input
                            id="companyBucket"
                            type="text"
                            value={companyBucket}
                            onChange={(e) => setCompanyBucket(e.target.value)}
                            className="form-input"
                            placeholder="ex: aliado-bucket"
                        />
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="defaultIaModel">Modelo de IA padrão</label>
                        <select
                            id="defaultIaModel"
                            value={defaultIaModel}
                            onChange={(e) => setDefaultIaModel(e.target.value)}
                            className="form-input"
                        >
                            <option value="">Selecione...</option>
                            {iaModels.filter((m) => !m.so_por_pergunta && !(m.so_slugs || []).length).map((m) => (
                                <option key={m.model_key} value={m.model_key}>{m.label}</option>
                            ))}
                        </select>
                        <span className="field-hint">Usado quando um prompt não define um modelo específico.</span>
                    </div>
                    <div className="form-group">
                        <label htmlFor="transcriptionEngine">Motor de transcrição</label>
                        <select
                            id="transcriptionEngine"
                            value={transcriptionEngine}
                            onChange={(e) => setTranscriptionEngine(e.target.value)}
                            className="form-input"
                        >
                            {Object.entries(MOTORES).map(([k, label]) => (
                                <option key={k} value={k}>{label}</option>
                            ))}
                            {!MOTORES[transcriptionEngine] && (
                                <option value={transcriptionEngine}>{transcriptionEngine}</option>
                            )}
                        </select>
                        <span className="field-hint">
                            Vale para o modo padrão e para o AWON. O transcriber da Aliado liga sozinho quando chega
                            gravação (a primeira depois de ele desligar leva uns 2 min a mais). A troca vale na próxima
                            gravação no modo padrão e em até 10 min no AWON.
                        </span>
                    </div>
                    {/* ------------------------------------------------------------------------------------
                        "Data final de aquecimento" (`unlock_date`) SAIU DA TELA — §2.7 do plano de Fases.

                        Aquecimento é uma FASE, não um atributo de loja/empresa: quem decide quando o
                        placar abre é a fase com `locks_scoreboard`, e o /auth passa a computar o
                        `unlock_date` a partir dela (`scoreboard_open_on`).

                        O CAMPO SÓ SAIU DA TELA — a coluna continua no banco e continua VENCENDO quem
                        não está em programa, então nada muda para as 9 lojas que têm data hoje. O
                        estado é preservado no submit: o valor atual é reenviado igual, e nunca é
                        limpo por omissão.

                        Para uma loja que precise calibrar de agora em diante, o caminho é criar
                        programa com fase de Aquecimento (que trava). Se for preciso o campo de volta
                        antes disso, é só devolver este bloco — o back-end continua aceitando.
                        ------------------------------------------------------------------------ */}
                </div>

                {regra && (
                    <div className="cls-rules-box">
                        <h3>Régua de atendimento válido</h3>
                        <p className="cls-rules-sub">
                            Define o que conta como atendimento válido e não-válido nesta empresa. Os
                            <strong> pontos</strong> de cada balde continuam sendo configurados no editor de prompts.
                        </p>

                        <table className="cls-rules-table">
                            <thead>
                                <tr><th>Classificação</th><th>Válido</th><th>Não-válido</th><th>Não conta</th></tr>
                            </thead>
                            <tbody>
                                {Object.keys(baldes).sort((a, b) => (a === '*' ? 1 : b === '*' ? -1 : a.localeCompare(b))).map((c) => (
                                    <tr key={c} className={c === '*' ? 'cls-catchall' : ''}>
                                        <td>
                                            <code>{c === '*' ? 'qualquer outra' : c}</code>
                                            {c === '*' && <span className="cls-rules-tip">classificação fora do catálogo</span>}
                                        </td>
                                        {['valid', 'invalid', 'none'].map((b) => (
                                            <td key={b}>
                                                <input
                                                    type="radio"
                                                    name={`balde-${c}`}
                                                    checked={baldes[c] === b}
                                                    onChange={() => setBaldes((s2) => ({ ...s2, [c]: b }))}
                                                />
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {regra.next && (
                            <p className="cls-rules-agendada">
                                ⏳ Já existe uma alteração agendada para <strong>{regra.next.valid_from}</strong>.
                                Salvar de novo hoje substitui essa alteração pendente.
                            </p>
                        )}

                        <button
                            type="button"
                            className="btn btn-submit"
                            disabled={!alterou}
                            onClick={() => setConfirmando(true)}
                        >
                            Salvar régua
                        </button>
                        {!alterou && <span className="cls-rules-tip"> Nenhuma alteração para salvar.</span>}
                    </div>
                )}

                {confirmando && (
                    <div className="cls-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="cls-modal-titulo">
                        <div className="cls-modal">
                            <h3 id="cls-modal-titulo">Confirmar alteração da régua</h3>

                            <p>
                                Você está mudando <strong>o que conta como atendimento válido</strong> em{' '}
                                <strong>{company.name}</strong>.
                            </p>

                            <div className="cls-modal-destaque">
                                <span className="cls-modal-data">Passa a valer em {vigenciaNova}</span>
                                <span>e vale <strong>de lá em diante, sempre</strong>.</span>
                            </div>

                            <ul className="cls-modal-lista">
                                <li>
                                    <strong>O histórico não muda.</strong> Todo atendimento já registrado continua
                                    contando pela régua que valia no dia em que foi gravado. Pontuação, contadores e
                                    ranking do passado ficam como estão.
                                </li>
                                <li>
                                    <strong>Metas não são recalculadas.</strong> Se a nova régua reduz o que conta como
                                    válido, as metas de atendimentos válidos ficam mais difíceis de bater sem terem sido
                                    alteradas. Revise-as se for o caso.
                                </li>
                                <li>
                                    <strong>Dá para corrigir até o fim do dia.</strong> Enquanto a alteração não entrar
                                    em vigor, salvar de novo substitui a pendente. Depois disso, só com uma alteração nova.
                                </li>
                            </ul>

                            <div className="cls-modal-acoes">
                                <button type="button" className="btn btn-cancel" onClick={() => setConfirmando(false)}>
                                    Cancelar
                                </button>
                                <button type="button" className="btn btn-submit" onClick={salvarRegra} disabled={salvandoRegra}>
                                    {salvandoRegra ? 'Salvando…' : 'Confirmar alteração'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <div className="form-actions">
                    <button type="button" className="btn btn-cancel" onClick={() => navigate(backUrl)}>
                        Cancelar
                    </button>
                    <button type="submit" className="btn btn-submit" disabled={submitting}>
                        {submitting ? 'Salvando...' : 'Salvar Empresa'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default CompanyEdit;
